import { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { C, TM } from "../lib/constants";
import { ini, isPremiumWorker } from "../lib/helpers";
import { Stars } from "./ui/Stars";
import { Btn } from "./ui/Btn";
import { Av } from "./ui/Av";
import { Chip } from "./ui/Chip";
import { TopNav } from "./ui/TopNav";
import { LoginRequiredBox } from "./ui/LoginRequiredBox";

export const PremiumBadge = ({ small=false }) => (
  <span
    style={{
      background:"linear-gradient(135deg,#F7C948,#D99A00)",
      color:"#3A2500",
      border:"1px solid #B98200",
      borderRadius:20,
      padding:small ? "2px 8px" : "5px 12px",
      fontSize:small ? 9 : 11,
      fontWeight:900,
      whiteSpace:"nowrap",
      boxShadow:"0 2px 8px rgba(217,154,0,0.25)",
      letterSpacing:0.2,
    }}
  >
    ⭐ Premium
  </span>
);

export const VerifiedBadge = ({ small=false }) => (
  <span
    style={{
      background:C.greenL,
      color:C.green,
      border:`1px solid ${C.green}35`,
      borderRadius:20,
      padding:small ? "2px 8px" : "5px 12px",
      fontSize:small ? 9 : 11,
      fontWeight:900,
      whiteSpace:"nowrap",
    }}
  >
    ✓ Verificado
  </span>
);

export const Bar = ({label, value, color}) => (
  <div style={{marginBottom:10}}>
    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:C.sub,fontSize:12}}>{label}</span><span style={{color,fontSize:12,fontWeight:700}}>{value}/5</span></div>
    <div style={{background:C.faint,borderRadius:4,height:5}}><div style={{background:color,borderRadius:4,height:5,width:`${value/5*100}%`}}/></div>
  </div>
);

export const WorkerProfile = ({selected, setScreen, setRequestWorker, user}) => {
  const [tab,          setTab]          = useState("info");
  const [rating,       setRating]       = useState(5);
  const [calidad,      setCalidad]      = useState(5);
  const [puntualidad,  setPuntualidad]  = useState(5);
  const [precioR,      setPrecioR]      = useState(5);
  const [limpieza,     setLimpieza]     = useState(5);
  const [comment,      setComment]      = useState("");
  const [sent,         setSent]         = useState(false);
  const [sending,      setSending]      = useState(false);
  const [reviewError,  setReviewError]  = useState("");
  const [solicitudFinalizada, setSolicitudFinalizada] = useState(null);
  const [checkingElegible,    setCheckingElegible]    = useState(false);

  useEffect(() => {
    if (!user?.id || !selected) return;
    const wData = selected?.data ?? selected ?? {};
    const uid = wData.user_id;
    const wid = wData.id;
    // Solo consultar si tenemos al menos un identificador real
    if (!uid && !wid) { setCheckingElegible(false); return; }
    setCheckingElegible(true);
    let filter = uid ? `trabajador_user_id.eq.${uid}` : `trabajador_id.eq.${wid}`;
    if (uid && wid) filter = `trabajador_user_id.eq.${uid},trabajador_id.eq.${wid}`;
    supabase
      .from("solicitudes")
      .select("id, status, resena_enviada, trabajador_nombre")
      .eq("cliente_user_id", user.id)
      .in("status", ["finalizada","cerrada"])
      .eq("resena_enviada", false)
      .or(filter)
      .limit(1)
      .maybeSingle()
      .then(({ data }) => { setSolicitudFinalizada(data || null); setCheckingElegible(false); })
      .catch(() => { setSolicitudFinalizada(null); setCheckingElegible(false); });
  }, [user?.id, selected]);

  if(!selected) return null;
  if(!user) return <LoginRequiredBox setScreen={setScreen} title="Perfil protegido" desc="Inicia sesión para revisar el perfil completo del trabajador, solicitar cotización y desbloquear el contacto por WhatsApp."/>;
  // PASO 39: defensiva — si selected viene sin .data (compatibilidad) lo manejamos igual
  const w = selected?.data ?? selected ?? {};
  const clientReviews = Array.isArray(w.clientReviews) ? w.clientReviews : [];
  const badges = Array.isArray(w.badges) ? w.badges : [];
  const portfolio = Array.isArray(w.portfolio) ? w.portfolio : [];
  const m = TM[w.trade]||{color:C.accent,icon:"🔧"};
  const avgCR = clientReviews.length?(clientReviews.reduce((a,r)=>a+r.rating,0)/clientReviews.length).toFixed(1):"—";
  const tb = (key,label) => <button onClick={()=>setTab(key)} style={{flex:1,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",borderRadius:10,padding:"9px 4px",background:tab===key?m.color:"transparent",color:tab===key?"#fff":C.muted}}>{label}</button>;

  return (
    <div style={{minHeight:"100vh",background:C.bg,paddingBottom:100}}>
      <TopNav setScreen={setScreen} back="directory" backLabel="Directorio" title={`${m.icon} ${w.trade}`}/>

      <div style={{background:`linear-gradient(180deg,${m.color}15 0%,${C.bg} 100%)`,padding:"24px 20px 0"}}>
        <div style={{maxWidth:640,margin:"0 auto"}}>
          <div style={{display:"flex",gap:16,alignItems:"flex-start",marginBottom:18}}>
            <Av letters={w.avatar} color={m.color} size={68} dot={w.available}/>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                <span style={{fontFamily:"'Georgia',serif",fontSize:20,fontWeight:800,color:C.text}}>{w.name}</span>
                {w.verified&&<VerifiedBadge small/>}
            {isPremiumWorker(w)&&<PremiumBadge small/>}
              </div>
              <div style={{color:m.color,fontSize:13,fontWeight:700,marginBottom:3}}>{w.specialty}</div>
              <div style={{color:C.muted,fontSize:12}}>📍 {w.zone} · {w.exp} años exp.</div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:18}}>
            {[{v:w.rating,l:"Calificación",s:`${w.reviews} reseñas`},{v:w.jobs,l:"Trabajos",s:"completados"},{v:avgCR,l:"Calif. clientes",s:`${clientReviews.length} cal.`}].map(s=>(
              <div key={s.l} style={{background:C.surface,borderRadius:14,padding:"14px 10px",textAlign:"center",border:`1px solid ${C.border}`,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
                <div style={{fontFamily:"'Georgia',serif",fontSize:22,fontWeight:800,color:m.color}}>{s.v}</div>
                <div style={{color:C.sub,fontSize:10,marginTop:2}}>{s.l}</div>
                <div style={{color:C.muted,fontSize:9}}>{s.s}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{maxWidth:640,margin:"0 auto",padding:"0 20px"}}>
        <div style={{display:"flex",gap:4,background:C.faint,borderRadius:12,padding:4,marginBottom:18}}>
          {tb("info","⭐ Calificación")}{tb("portfolio","📸 Portafolio")}{tb("clients","👤 Clientes")}{tb("review","✏️ Calificar")}
        </div>

        {tab==="info" && (
          <div>
            <div style={{background:C.surface,borderRadius:16,padding:18,marginBottom:14,border:`1px solid ${C.border}`,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
              <div style={{color:C.sub,fontSize:11,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",marginBottom:14}}>Desglose de calificación</div>
              <Bar label="Calidad del trabajo" value={4.9} color={m.color}/>
              <Bar label="Puntualidad" value={4.7} color={m.color}/>
              <Bar label="Precio justo" value={4.6} color={m.color}/>
              <Bar label="Limpieza" value={4.8} color={m.color}/>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
              {badges.map(b=><Chip key={b} text={b} color={m.color}/>)}
              <Chip text={w.available?"● Disponible":"○ Ocupado"} color={w.available?C.green:C.muted}/>
              <Chip text={w.membresia==="activa"?"✓ Membresía activa":"⚠ Membresía vencida"} color={w.membresia==="activa"?C.green:C.red}/>
            </div>
            <button
              onClick={()=>setRequestWorker(w)}
              style={{
                width:"100%",
                background:C.accent,
                border:"none",
                color:"#fff",
                borderRadius:14,
                padding:15,
                textAlign:"center",
                fontWeight:900,
                fontSize:14,
                cursor:"pointer",
                boxShadow:`0 4px 14px ${C.accent}35`
              }}
            >
              📋 Solicitar cotización
            </button>
            <a
              href={`https://wa.me/52${String(w.phone || w.telefono || "").replace(/\D/g, "")}?text=${encodeURIComponent(
                "Hola 👋 Vi tu perfil en FICIO y me gustaría hablar contigo sobre un trabajo. ¿Podrías brindarme más información?"
              )}`}
              target="_blank"
              rel="noreferrer"
              style={{
                display:"block",
                background:"#25D366",
                color:"#fff",
                textDecoration:"none",
                borderRadius:14,
                padding:14,
                textAlign:"center",
                fontWeight:800
              }}
            >
              💬 WhatsApp directo
            </a>
            <button onClick={()=>setScreen("obra-activa")} style={{display:"block",width:"100%",background:C.greenL,border:`1.5px solid ${C.green}40`,borderRadius:14,padding:16,textAlign:"center",fontWeight:700,fontSize:14,fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",color:C.green,cursor:"pointer"}}>
              🏗️ Gestionar obra activa · Cobertura Ficio
            </button>
          </div>
        )}

        {tab==="portfolio" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{color:C.sub,fontSize:13,fontWeight:600}}>{portfolio?.length||0} trabajos</div>
              <Chip text="Obras verificadas" color={m.color} small/>
            </div>
            {(!portfolio||portfolio.length===0) ? (
              <div style={{textAlign:"center",padding:"48px 0",color:C.muted}}><div style={{fontSize:40,marginBottom:12}}>📸</div><div>Sin fotos aún</div></div>
            ) : (
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                {portfolio.map((p,i)=>(
                  <div key={i} style={{borderRadius:16,overflow:"hidden",border:`1px solid ${C.border}`,cursor:"pointer",transition:"transform 0.2s",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}
                    onMouseEnter={e=>e.currentTarget.style.transform="scale(1.02)"}
                    onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
                    <div style={{height:130,background:`linear-gradient(135deg,${m.color},${m.color}88,${m.color}44)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,position:"relative",overflow:"hidden"}}>
                      {p.url ? (
                        <img src={p.url} alt={p.title || "Trabajo"} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                      ) : (
                        <span style={{filter:"drop-shadow(0 2px 6px rgba(0,0,0,0.3))"}}>{p.emoji || "📸"}</span>
                      )}
                      <div style={{position:"absolute",top:8,left:8,background:"rgba(255,255,255,0.92)",borderRadius:6,padding:"2px 8px",fontSize:9,color:m.color,fontWeight:800}}>OBRA</div>
                    </div>
                    <div style={{background:C.surface,padding:"10px 12px"}}>
                      <div style={{color:C.text,fontSize:12,fontWeight:700,marginBottom:2}}>{p.title || `Trabajo ${i+1}`}</div>
                      <div style={{color:C.muted,fontSize:10}}>{p.desc || "Foto de portafolio"}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab==="clients" && (
          <div>
            <div style={{background:C.greenL,border:`1.5px solid ${C.green}30`,borderRadius:12,padding:"12px 16px",marginBottom:14}}>
              <div style={{color:C.sub,fontSize:12}}>💡 Así calificó este maestro a sus clientes.</div>
            </div>
            {clientReviews.length===0 ? (
              <div style={{textAlign:"center",padding:"40px 0",color:C.muted}}><div style={{fontSize:32,marginBottom:8}}>👤</div><div>Aún no ha calificado clientes</div></div>
            ) : clientReviews.map((r,i)=>(
              <div key={i} style={{background:C.surface,borderRadius:16,padding:16,marginBottom:10,border:`1px solid ${C.border}`,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <Av letters={ini(r.cName)} color={r.cRating>=4.5?C.green:C.accent} size={38}/>
                    <div>
                      <div style={{color:C.text,fontSize:13,fontWeight:700}}>{r.cName}</div>
                      <div style={{display:"flex",alignItems:"center",gap:4,marginTop:2}}><Stars rating={r.cRating} size={10}/><span style={{color:C.muted,fontSize:10}}>como cliente</span></div>
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}><Stars rating={r.rating} size={11}/><div style={{color:C.muted,fontSize:10,marginTop:2}}>{r.date}</div></div>
                </div>
                <p style={{color:C.sub,fontSize:13,lineHeight:1.5,margin:0,fontStyle:"italic"}}>"{r.comment}"</p>
              </div>
            ))}
          </div>
        )}

        {tab==="review" && (
          <div style={{background:C.surface,borderRadius:16,padding:20,border:`1px solid ${C.border}`,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
            <div style={{fontFamily:"'Georgia',serif",fontSize:18,fontWeight:800,color:C.text,marginBottom:4}}>Califica a {w.name.split(" ")[0]}</div>

            {!user ? (
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <div style={{fontSize:40,marginBottom:8}}>🔐</div>
                <div style={{color:C.sub,fontSize:13,marginBottom:14}}>Inicia sesión para calificar</div>
                <Btn full color={m.color} onClick={()=>setScreen("cliente-login")}>Iniciar sesión</Btn>
              </div>
            ) : sent ? (
              <div style={{textAlign:"center",padding:"24px 0"}}>
                <div style={{fontSize:48,marginBottom:8}}>✅</div>
                <div style={{fontFamily:"'Georgia',serif",fontWeight:800,color:C.green,fontSize:18}}>¡Reseña enviada!</div>
                <div style={{color:C.muted,fontSize:13,marginTop:4}}>Gracias por contribuir a la comunidad Ficio</div>
              </div>
            ) : checkingElegible ? (
              <div style={{textAlign:"center",padding:"20px 0",color:C.muted,fontSize:13}}>Verificando elegibilidad...</div>
            ) : !solicitudFinalizada ? (
              <div style={{background:C.accentL,border:`1.5px solid ${C.accent}25`,borderRadius:14,padding:18,textAlign:"center"}}>
                <div style={{fontSize:36,marginBottom:10}}>🔒</div>
                <div style={{fontFamily:"'Georgia',serif",fontSize:16,fontWeight:800,color:C.text,marginBottom:6}}>Reseña verificada</div>
                <div style={{color:C.sub,fontSize:13,lineHeight:1.6}}>Solo los clientes que contrataron y finalizaron un trabajo con <strong>{w.name.split(" ")[0]}</strong> pueden dejar reseña. Esto garantiza que todas las calificaciones son reales.</div>
              </div>
            ) : (
              <>
                {reviewError && <div style={{background:C.redL,border:`1px solid ${C.red}30`,borderRadius:10,padding:"10px 12px",color:C.red,fontSize:12,marginBottom:12}}>{reviewError}</div>}
                <div style={{color:C.muted,fontSize:12,marginBottom:16}}>
                  ✅ Tienes un trabajo finalizado con {w.name.split(" ")[0]} — tu reseña será verificada
                </div>

                {/* Calificación general */}
                <div style={{marginBottom:16}}>
                  <div style={{color:C.sub,fontSize:11,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",marginBottom:8}}>Calificación general</div>
                  <div style={{display:"flex",gap:6}}>
                    {[1,2,3,4,5].map(s=>(
                      <button key={s} onClick={()=>setRating(s)} style={{background:"none",border:"none",cursor:"pointer",fontSize:32,opacity:s<=rating?1:0.25,transform:s<=rating?"scale(1.1)":"scale(1)",transition:"all 0.15s"}}>⭐</button>
                    ))}
                  </div>
                </div>

                {/* Subcategorías */}
                {[
                  ["Calidad del trabajo", calidad, setCalidad, "🔨"],
                  ["Puntualidad",         puntualidad, setPuntualidad, "⏰"],
                  ["Precio justo",        precioR, setPrecioR, "💰"],
                  ["Limpieza",            limpieza, setLimpieza, "🧹"],
                ].map(([label, val, setter, icon]) => (
                  <div key={label} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                      <span style={{color:C.sub,fontSize:12}}>{icon} {label}</span>
                      <span style={{color:m.color,fontSize:12,fontWeight:700}}>{val}/5</span>
                    </div>
                    <div style={{display:"flex",gap:4}}>
                      {[1,2,3,4,5].map(s=>(
                        <button key={s} onClick={()=>setter(s)}
                          style={{flex:1,height:6,borderRadius:4,border:"none",cursor:"pointer",
                            background:s<=val?m.color:C.faint,transition:"background 0.15s"}}/>
                      ))}
                    </div>
                  </div>
                ))}

                <textarea value={comment} onChange={e=>setComment(e.target.value)}
                  placeholder="¿Cómo fue tu experiencia? Cuéntale a otros clientes..."
                  rows={4}
                  style={{width:"100%",background:C.bg,border:`1.5px solid ${C.border}`,borderRadius:12,color:C.text,padding:"12px 14px",fontSize:13,resize:"none",boxSizing:"border-box",outline:"none",fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",marginBottom:14,marginTop:12}}
                  onFocus={e=>e.target.style.borderColor=m.color}
                  onBlur={e=>e.target.style.borderColor=C.border}/>

                <Btn full color={m.color} onClick={async () => {
                  if (!comment.trim()) { setReviewError("Escribe un comentario para enviar tu reseña."); return; }
                  setReviewError("");
                  setSending(true);
                  try {
                    // 1. Insertar la reseña
                    const { error: resenaError } = await supabase
                      .from("resenas")
                      .insert([{
                        solicitud_id:    solicitudFinalizada.id,
                        autor_id:        user.id,
                        destinatario_id: w.user_id || w.id,
                        calificacion:    rating,
                        comentario:      comment.trim(),
                        tipo:            "cliente_a_trabajador",
                      }]);

                    if (resenaError) throw resenaError;

                    // 2. Marcar solicitud completada
                    await supabase
                      .from("solicitudes")
                      .update({ estado: "completada", updated_at: new Date().toISOString() })
                      .eq("id", solicitudFinalizada.id);

                    // 3. Actualizar calificación promedio del trabajador
                    const { data: todasResenas } = await supabase
                      .from("resenas")
                      .select("calificacion")
                      .eq("destinatario_id", w.user_id || w.id)
                      .eq("tipo", "cliente_a_trabajador");

                    if (todasResenas?.length && w.user_id) {
                      const promedio = todasResenas.reduce((a,r) => a + r.calificacion, 0) / todasResenas.length;
                      await supabase.from("trabajadores")
                        .update({ calificacion: promedio, "reseñas": todasResenas.length })
                        .eq("user_id", w.user_id);
                    }

                    setSent(true);
                  } catch(e) {
                    setReviewError(e.message || "Error al enviar la reseña. Inténtalo de nuevo.");
                  } finally {
                    setSending(false);
                  }
                }}>
                  {sending ? "Enviando..." : "Enviar reseña verificada ✓"}
                </Btn>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
